import { useEffect, useState } from "react"
import {
  getAdminOverviewApi, AdminOverview,
  getAdminUsersAnalyticsApi, AdminUsersAnalytics,
  getAdminCoursesApi, AdminCourseListItem,
} from "../../features/analytics/api/adminAnalytics.api"
import { VBarChart, HBarChart, DonutChart, ChartCard } from "./Charts"
import {
  UsersIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  ExclamationTriangleIcon,
} from "@heroicons/react/16/solid"

interface StatRowProps { label: string; value: string | number }
const StatRow = ({ label, value }: StatRowProps) => (
  <div className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
    <span className="text-sm text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-800">{typeof value === "number" ? value.toLocaleString("ru") : value}</span>
  </div>
)

interface CardProps {
  title: string
  icon: React.ReactNode
  accent: string
  iconBg: string
  big: string | number
  sub: string
  children: React.ReactNode
}
const Card = ({ title, icon, accent, iconBg, big, sub, children }: CardProps) => (
  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
    <div className={`${accent} px-5 py-4 flex items-center gap-3`}>
      <span className={`${iconBg} w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0`}>
        {icon}
      </span>
      <div>
        <p className="text-xs text-gray-400">{title}</p>
        <p className="text-2xl font-bold text-gray-900 leading-tight">
          {typeof big === "number" ? big.toLocaleString("ru") : big}
        </p>
        <p className="text-[11px] text-gray-400">{sub}</p>
      </div>
    </div>
    <div className="px-5 py-3">{children}</div>
  </div>
)

export const OverviewPage = () => {
  const [overview, setOverview] = useState<AdminOverview | null>(null)
  const [users, setUsers] = useState<AdminUsersAnalytics | null>(null)
  const [courses, setCourses] = useState<AdminCourseListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    Promise.all([
      getAdminOverviewApi(),
      getAdminUsersAnalyticsApi(),
      getAdminCoursesApi({ sort_by: "purchases", limit: 5 }),
    ])
      .then(([o, u, c]) => {
        setOverview(o.data)
        setUsers(u.data)
        setCourses(c.data.items)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить обзор</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading || !overview || !users) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 h-40 animate-pulse" />
        ))}
      </div>
    )
  }

  const d = overview

  const platformData = [
    { label: "Пользователи", value: d.users.total },
    { label: "Просм. курсов", value: d.courses.total_views },
    { label: "Просм. вакансий", value: d.vacancies.total_views },
    { label: "Просм. компаний", value: d.china_enterprises.total_views },
  ]

  const top5courses = [...courses].sort((a, b) => b.purchases - a.purchases).slice(0, 5)

  return (
    <div className="space-y-5">
      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <Card title="Пользователи" icon={<UsersIcon className="w-4 h-4 text-blue-600" />}
          accent="bg-blue-50/60" iconBg="bg-blue-100"
          big={d.users.total} sub="всего пользователей">
          <StatRow label="Новых за 30 дней"   value={d.users.new_last_30_days} />
          <StatRow label="Менторов"            value={d.users.mentors} />
          <StatRow label="Активных за 7 дней"  value={d.users.active_last_7_days} />
        </Card>

        <Card title="Курсы" icon={<AcademicCapIcon className="w-4 h-4 text-violet-600" />}
          accent="bg-violet-50/60" iconBg="bg-violet-100"
          big={d.courses.total_purchases} sub="покупок">
          <StatRow label="Всего курсов"  value={d.courses.total} />
          <StatRow label="Просмотров"    value={d.courses.total_views} />
          <StatRow label="Конверсия"     value={`${d.courses.avg_conversion_rate}%`} />
        </Card>

        <Card title="Вакансии" icon={<BriefcaseIcon className="w-4 h-4 text-emerald-600" />}
          accent="bg-emerald-50/60" iconBg="bg-emerald-100"
          big={d.vacancies.total} sub="вакансий">
          <StatRow label="Просмотров" value={d.vacancies.total_views} />
          <StatRow label="Заявок"     value={d.vacancies.total_applications} />
          <StatRow label="Конверсия"  value={`${d.vacancies.avg_application_rate}%`} />
        </Card>

        <Card title="Компании Китая" icon={<BuildingOffice2Icon className="w-4 h-4 text-red-600" />}
          accent="bg-red-50/60" iconBg="bg-red-100"
          big={d.china_enterprises.total} sub="компаний">
          <StatRow label="Просмотров профилей" value={d.china_enterprises.total_views} />
          <div className="py-2 border-b border-gray-50" />
          <div className="py-2" />
        </Card>
      </div>

      {/* Summary banner */}
      <div className="bg-gradient-to-r from-blue-600 to-violet-600 rounded-2xl p-5 text-white flex flex-wrap gap-6">
        {[
          { label: "Пользователей",   value: d.users.total.toLocaleString("ru") },
          { label: "Покупок курсов",  value: d.courses.total_purchases.toLocaleString("ru") },
          { label: "Заявок на вак.",  value: d.vacancies.total_applications.toLocaleString("ru") },
          { label: "Компаний Китая",  value: d.china_enterprises.total.toLocaleString("ru") },
        ].map(({ label, value }) => (
          <div key={label}>
            <p className="text-white/70 text-xs uppercase tracking-wider mb-1">{label}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <ChartCard title="Активность платформы (просмотры)">
          <VBarChart data={platformData} color="#3b82f6" />
        </ChartCard>

        <ChartCard title="Топ-5 курсов по покупкам">
          <HBarChart
            data={top5courses.map((c) => ({ label: c.title.slice(0, 20), value: c.purchases }))}
            color="#8b5cf6"
          />
        </ChartCard>
      </div>

      {/* Roles donut */}
      <ChartCard title="Распределение пользователей по ролям">
        <DonutChart data={[
          { label: "Обычные пользователи", value: users.by_role?.user ?? 0,   color: "#3b82f6" },
          { label: "Менторы",              value: users.by_role?.mentor ?? 0, color: "#8b5cf6" },
          { label: "Администраторы",       value: users.by_role?.admin ?? 0,  color: "#f59e0b" },
        ]} />
      </ChartCard>
    </div>
  )
}
